import { ChangeEvent, FC } from 'react';

import { Controller, useFormContext } from 'react-hook-form';

import Checkbox from 'components/CheckboxGroup/Checkbox/Checkbox';
import {
  DENTITION_TYPE,
  DENTITION_TYPE_OPTIONS
} from 'constants/prescriptionOptions';
import { Option } from 'types/select';
import { TeethExtraction } from 'types/TreatmentPlanning.types';
import { translate } from 'utils';

import RhfToothChart from '../ToothChart/RhfToothChart';

interface Props {
  dentitionSelectionName: string;
  adultChartName: string;
  primaryChartName: string;
  disabled?: boolean;
  isUpperDisabled?: boolean;
  isLowerDisabled?: boolean;
  isToothChartRestricted?: boolean;
  restrictedAdultToothChartValue?: TeethExtraction;
  restrictedPrimaryToothChartValue?: TeethExtraction;
  isCrowdExtraction?: boolean;
  checkBoxClassName?: string;
  checkBoxItemFont?: string;
}

const DentitionTypeWithToothCharts: FC<Props> = ({
  dentitionSelectionName,
  adultChartName,
  primaryChartName,
  disabled,
  isUpperDisabled,
  isLowerDisabled,
  isToothChartRestricted,
  restrictedAdultToothChartValue,
  restrictedPrimaryToothChartValue,
  isCrowdExtraction,
  checkBoxClassName = '',
  checkBoxItemFont = 'md:text-17px text-sm'
}) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={dentitionSelectionName}
      control={control}
      render={({ field }) => {
        const selectedItems: Option[] = field.value || [];

        const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
          const { checked, value } = e.target;
          if (checked) {
            const currentSelection = DENTITION_TYPE_OPTIONS.find(
              (option) => option?.value === value
            );
            if (currentSelection) {
              field.onChange([...selectedItems, currentSelection]);
            }
          } else {
            field.onChange(
              selectedItems.filter((option) => option?.value !== value)
            );
          }
        };

        const isChecked = (value: string) =>
          selectedItems?.findIndex((item) => item.value === value) > -1;

        return (
          <div className='flex flex-col gap-y-6'>
            {DENTITION_TYPE_OPTIONS.map((option) => {
              const isAdult = option.value === DENTITION_TYPE.ADULT_DENTITION;
              const isSelected = isChecked(option.value);

              return (
                <div key={option.value} className='flex flex-col'>
                  <label className='py-2 flex' role='presentation'>
                    <Checkbox
                      data-testid='checkbox'
                      id={`${dentitionSelectionName}-${option.value}`}
                      name={dentitionSelectionName}
                      value={option.value}
                      onChange={handleChange}
                      checked={isSelected}
                      className={checkBoxClassName}
                      disabled={disabled}
                    />
                    <span className={`pt-0.5 ${checkBoxItemFont}`}>
                      {translate(option.label as string)}
                    </span>
                  </label>
                  {isSelected && (
                    <div className='mt-4 ml-5'>
                      <RhfToothChart
                        isBaby={!isAdult}
                        showError={false}
                        name={isAdult ? adultChartName : primaryChartName}
                        disabled={disabled}
                        isUpperDisabled={isUpperDisabled}
                        isLowerDisabled={isLowerDisabled}
                        isToothChartRestricted={isToothChartRestricted}
                        restrictedToothChartValue={
                          isAdult
                            ? restrictedAdultToothChartValue
                            : restrictedPrimaryToothChartValue
                        }
                        isCrowdExtraction={isCrowdExtraction}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      }}
    />
  );
};

export default DentitionTypeWithToothCharts;
